import React from 'react';
import {
  Typography,
  Box,
  Skeleton,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';
import { Trophy, ArrowDown } from 'lucide-react';
import { PerformanceCardProps } from '../types/analytics.types';

export const PerformanceCard: React.FC<PerformanceCardProps> = ({
  data,
  config,
  type,
  title,
  loading = false,
  className,
  tallHeight = false,
  cardCount = 3
}) => {
  const cardHeight = tallHeight ? '9.5rem' : '7.625rem'; // 152px when tall, 122px normal

  if (loading) {
    return (
      <Box
        sx={{
          backgroundColor: '#ffffff',
          border: '1px solid #e4e4e7',
          borderRadius: '8px',
          padding: '1rem',
          height: cardHeight || '7.625rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          width: '100%',
        }}
      >
        <Skeleton variant="text" width="60%" height={20} />
        <Skeleton variant="text" width="80%" height={24} />
        <Skeleton variant="text" width="40%" height={24} />
      </Box>
    );
  }

  // Graceful fallback: render card with 0 value if no data
  const hasData = Array.isArray(data) && data.length > 0;
  const performanceData: any = hasData ? data[0] : {};
  const metricValue = Number(performanceData[config.metricField] ?? 0);
  const comparisonValue = config.comparisonField ? (performanceData[config.comparisonField] ?? null) : null;

  // For calculated mode, show location names if available in the data
  // If displayField is specified in config, use that field first
  // Otherwise, prioritize location-related fields over register to get actual location names
  const locationName = config.mode === 'calculated' ?
    (config.displayField ? (performanceData[config.displayField] || null) :
      (performanceData.location_name || performanceData.location || performanceData.revenue_center || performanceData.location_zone || performanceData.zone || performanceData.name || performanceData.locationId || performanceData.register || null)) :
    (config.displayField ? (performanceData[config.displayField] || null) :
      (performanceData.location_name || performanceData.location || performanceData.revenue_center || performanceData.zone || performanceData.location_zone || performanceData.name || performanceData.locationId || performanceData.register || (hasData ? 'Unknown' : null)));

  // Format the metric value
  const formatValue = (value: number): string => {
    if (config.metricField.includes('avg_order_value') || config.metricField.includes('avg_guest_value')) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    }

    if (config.metricField.includes('revenue') || config.metricField.includes('sales')) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    }

    if (config.metricField.includes('percentage') || config.metricField.includes('percent')) {
      return `${value?.toFixed(1)}%`;
    }

    return value?.toLocaleString();
  };

  // Calculate change direction and format
  const getChangeInfo = (changeValue: number): { direction: 'up' | 'down' | 'neutral'; changeText: string } => {
    const direction: 'up' | 'down' | 'neutral' = changeValue > 0 ? 'up' : changeValue < 0 ? 'down' : 'neutral';
    const formattedChange = Math.abs(changeValue);
    const changeText = `${formattedChange.toFixed(1)}%`; // No + or - sign

    return { direction, changeText };
  };

  const changeInfo = comparisonValue !== null ? getChangeInfo(comparisonValue) : null;

  // For top performers, always show green icon and text (even if no change data)
  // For low performers, always show red icon and text (even if no change data)
  // Get card title - use provided title prop if available, otherwise generate dynamically
  const getCardTitle = () => {
    // If title is provided from JSON config, use it
    if (title) {
      return title;
    }

    // Otherwise, fall back to dynamic generation based on metricField
    if (config.metricField.includes('order_count')) {
      return type === 'top' ? 'Top Order Volume' : 'Lowest Order Volume';
    } else if (config.metricField.includes('avg_order_value')) {
      return type === 'top' ? 'Highest Avg Order' : 'Lowest Avg Order';
    } else if (config.metricField.includes('total_net_sales') && performanceData.register) {
      return type === 'top' ? 'Top Grossing Register' : 'Lowest Grossing Register';
    } else if (config.metricField.includes('zone_order_count') || config.metricField.includes('zone_net_sales')) {
      return type === 'top' ? 'Top Zone Performer' : 'Lowest Zone Performer';
    } else if (config.metricField.includes('sales') && performanceData.revenue_center) {
      return type === 'top' ? 'Top Revenue Center' : 'Lowest Revenue Center';
    } else if (config.metricField.includes('orders') && performanceData.revenue_center) {
      return type === 'top' ? 'Top Order Volume' : 'Lowest Order Volume';
    } else {
      return type === 'top' ? 'Top Performer' : 'Low Performer';
    }
  };

  const cardTitle = getCardTitle();
  // Responsive font sizes based on card count (more cards = smaller text)
  // Scale font sizes based on card count: 3-4 cards = normal, 5-6 = smaller, 7+ = smallest
  const getFontSize = (baseMin: string, baseVw: string, baseMax: string) => {
    if (cardCount >= 7) {
      return `clamp(${baseMin}, calc(${baseVw} * 0.7), calc(${baseMax} * 0.75))`;
    } else if (cardCount >= 5) {
      return `clamp(${baseMin}, calc(${baseVw} * 0.85), calc(${baseMax} * 0.875))`;
    }
    return `clamp(${baseMin}, ${baseVw}, ${baseMax})`;
  };

  const titleFontSize = getFontSize('0.75rem', '1.75vw', '0.875rem');
  const locationFontSize = getFontSize('0.75rem', '2vw', '1rem');
  const metricFontSize = getFontSize('0.875rem', '2vw', '1rem');

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: cardHeight,
        minHeight: cardHeight,
        padding: '1rem',
        boxSizing: 'border-box',
      }}
      className={className}
    >
      {/* Trend indicators - positioned at top right */}
      <Box
        sx={{
          position: 'absolute',
          top: '1rem',
          right: '1rem',
          display: 'flex',
          gap: '0.25rem',
          alignItems: 'center',
          flexShrink: 0,
          zIndex: 1,
        }}
      >

      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          minWidth: 0,
          width: '100%',
        }}
      >
        {/* Header Row: Trophy Icon, Title */}
        <Box
          sx={{
            display: 'flex',
            gap: '0.25rem',
            alignItems: 'center',
            width: '100%',
            paddingRight: '3rem', // Space for trend indicator
          }}
        >
          {/* Trophy Icon for Top, ArrowDown for Low */}
          {type === 'top' ? (
            <Trophy
              size={16}
              color="#71717a"
              style={{ flexShrink: 0, width: '1rem', height: '1rem' }}
            />
          ) : (
            <ArrowDown
              size={16}
              color="#71717a"
              style={{ flexShrink: 0, width: '1rem', height: '1rem' }}
            />
          )}

          {/* Title */}
          <Typography
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 500, // medium
              fontSize: titleFontSize,
              lineHeight: '1.25rem',
              color: '#71717a',
              flex: 1,
              minWidth: 0,
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            {cardTitle}
          </Typography>
        </Box>

        {/* Location Name */}
        {locationName && (
          <Typography
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 600, // semibold
              fontSize: locationFontSize,
              lineHeight: '2rem',
              color: '#18181b',
              width: '100%',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            {locationName}
          </Typography>
        )}

        {/* Metric Value */}
        <Box
          sx={{
            display: 'flex',
            gap: '0.25rem',
            alignItems: 'flex-start',
            width: '100%',
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 700, // bold
              fontSize: metricFontSize,
              lineHeight: 1, // leading-none
              color: '#71717a',
              flex: 1,
              minWidth: 0,
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            {formatValue(metricValue)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}; 